// pages/Report/SalonAttendantReport.js
import React,{useState,useEffect,useCallback} from 'react';
import{useAuth}from'../../common/context/AuthContext';
import{SALON_API_URL}from'../../api/config';
import{Users,TrendingUp,Hash,Calendar,X,Loader2,Scissors,Award,ChevronDown,ChevronUp}from'lucide-react';

function fmt(n){return'₹'+Number(n||0).toLocaleString('en-IN',{minimumFractionDigits:0,maximumFractionDigits:2});}
function fmtDate(d){try{return new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'});}catch{return'—';}}
function toLocal(s,end=false){const d=new Date(s);if(end)d.setHours(23,59,59,999);else d.setHours(0,0,0,0);return d.toISOString();}

const FILTERS=[{id:'today',label:'Today'},{id:'yesterday',label:'Yesterday'},{id:'week',label:'This Week'},{id:'month',label:'This Month'},{id:'3months',label:'Last 3 Months'},{id:'custom',label:'Custom'}];
const SORT_OPTIONS=[{id:'services',label:'Services'},{id:'revenue',label:'Revenue'}];

function getRange(id){
  const n=new Date(),y=n.getFullYear(),m=n.getMonth(),d=n.getDate();
  if(id==='today')return{start:new Date(y,m,d,0,0,0),end:new Date(y,m,d,23,59,59,999)};
  if(id==='yesterday')return{start:new Date(y,m,d-1,0,0,0),end:new Date(y,m,d-1,23,59,59,999)};
  if(id==='week'){const day=n.getDay();return{start:new Date(y,m,d-(day===0?6:day-1),0,0,0),end:new Date(y,m,d,23,59,59,999)};}
  if(id==='month')return{start:new Date(y,m,1,0,0,0),end:new Date(y,m,d,23,59,59,999)};
  if(id==='3months')return{start:new Date(y,m-3,d,0,0,0),end:new Date(y,m,d,23,59,59,999)};
  return null;
}
function rangeLabel(id,custom){
  if(id==='custom'&&custom.start&&custom.end)return`${fmtDate(custom.start)} → ${fmtDate(custom.end)}`;
  const r=getRange(id);return r?`${fmtDate(r.start)} → ${fmtDate(r.end)}`:'';
}
function extractServiceItems(bill){
  if(Array.isArray(bill.items)&&bill.items.length>0)return bill.items.filter(i=>i.item_type==='service');
  return(bill.services||[]).map(s=>({...s,item_type:'service',item_name:s.item_name||s.service_name||s.name||'Service',item_variant:s.item_variant||s.variant||null,price:parseFloat(s.price||0)}));
}

const AVATAR_COLORS=['bg-violet-500','bg-blue-500','bg-emerald-500','bg-rose-500','bg-amber-500','bg-cyan-500','bg-pink-500'];
function initials(name=''){return name.trim().split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2);}
function avatarBg(name=''){return AVATAR_COLORS[(name.charCodeAt(0)||0)%AVATAR_COLORS.length];}

function CustomDateModal({open,onClose,onApply}){
  const[start,setStart]=useState('');const[end,setEnd]=useState('');const[err,setErr]=useState('');
  if(!open)return null;
  const max=new Date().toISOString().split('T')[0];
  return(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-sm p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-black text-gray-900 dark:text-white">Custom Range</h3>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 text-gray-400"><X size={16}/></button>
        </div>
        <div className="space-y-4">
          <div><label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-1.5">From</label>
            <input type="date" max={max} value={start} onChange={e=>setStart(e.target.value)} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-800"/></div>
          <div><label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-1.5">To</label>
            <input type="date" max={max} min={start} value={end} onChange={e=>setEnd(e.target.value)} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-800"/></div>
          {err&&<p className="text-xs text-red-500">{err}</p>}
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-gray-600">Cancel</button>
          <button onClick={()=>{if(!start||!end){setErr('Select both dates');return;}if(start>end){setErr('Start must be before end');return;}onApply(start,end);onClose();}} className="flex-1 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold">Apply</button>
        </div>
      </div>
    </div>
  );
}

function StaffCard({member,rank,maxRevenue}){
  const[expanded,setExpanded]=useState(false);
  const barW=maxRevenue>0?(member.revenue/maxRevenue)*100:0;
  const medal=rank===1?'🥇':rank===2?'🥈':rank===3?'🥉':null;
  return(
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 overflow-hidden hover:shadow-sm transition-shadow">
      <button onClick={()=>setExpanded(e=>!e)} className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors text-left">
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="w-7 text-center">{medal||<span className="text-sm font-black text-gray-300">#{rank}</span>}</div>
          <div className={`w-11 h-11 rounded-2xl ${avatarBg(member.name)} flex items-center justify-center text-white font-black text-sm`}>{initials(member.name)}</div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-base font-bold text-gray-800 dark:text-white truncate">{member.name}</p>
          <div className="flex-1 h-2 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden mt-1">
            <div className="h-full bg-purple-500 rounded-full" style={{width:`${barW}%`}}/>
          </div>
          <div className="flex items-center gap-4 mt-1">
            <span className="text-xs text-gray-400">{member.services} services</span>
            {member.helperCount>0&&<span className="text-xs text-blue-400">{member.helperCount}× helper</span>}
            {member.skillLevel&&<span className="text-xs text-gray-400 capitalize">{member.skillLevel}</span>}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-lg font-black text-gray-800 dark:text-white">{fmt(member.revenue)}</p>
          <p className="text-xs text-gray-400">revenue</p>
        </div>
        <div className={`w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0 ${expanded?'bg-purple-100':'bg-gray-100 dark:bg-slate-700'}`}>
          {expanded?<ChevronUp size={14} className="text-purple-600"/>:<ChevronDown size={14} className="text-gray-400"/>}
        </div>
      </button>
      {expanded&&(
        <div className="border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/40 px-5 py-4">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Services Performed</p>
          {member.serviceList.length===0
            ?<p className="text-sm text-gray-400">No service data for this period</p>
            :<div className="space-y-2">
              {member.serviceList.slice(0,10).map((svc,i)=>(
                <div key={i} className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className="w-6 h-6 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                      <Scissors size={11} className="text-purple-500"/>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 truncate">{svc.name}{svc.variant?` · ${svc.variant}`:''}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-xs text-gray-400">{svc.count}×</span>
                    <span className="text-sm font-bold text-gray-800 dark:text-white">{fmt(svc.revenue)}</span>
                  </div>
                </div>
              ))}
              {member.serviceList.length>10&&<p className="text-xs text-gray-400">+{member.serviceList.length-10} more</p>}
            </div>
          }
          {member.helperCount>0&&(
            <div className="mt-4 pt-3 border-t border-gray-200 dark:border-slate-600">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Helper Role</p>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Users size={11} className="text-blue-500"/>
                </div>
                <span className="text-sm text-gray-700 dark:text-gray-300">Assisted in <strong>{member.helperCount}</strong> services as helper</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SalonAttendantReport(){
  const{userToken:token}=useAuth();
  const[activeFilter,setActiveFilter]=useState('month');
  const[custom,setCustom]=useState({start:'',end:''});
  const[showCustom,setShowCustom]=useState(false);
  const[sortBy,setSortBy]=useState('revenue');
  const[loading,setLoading]=useState(true);
  const[bills,setBills]=useState([]);
  const[staffList,setStaffList]=useState([]);

  const fetchData=useCallback(async()=>{
    if(!token)return;setLoading(true);
    try{
      const[staffRes]=await Promise.all([
        fetch(`${SALON_API_URL}/staff`,{headers:{Authorization:`Bearer ${token}`}})
      ]);
      const staffData=await staffRes.json();
      if(staffData.success)setStaffList(staffData.data||[]);

      let start,end;
      if(activeFilter==='custom'){if(!custom.start||!custom.end){setLoading(false);return;}start=toLocal(custom.start,false);end=toLocal(custom.end,true);}
      else{const r=getRange(activeFilter);start=r.start.toISOString();end=r.end.toISOString();}
      const billRes=await fetch(`${SALON_API_URL}/bills?startDate=${encodeURIComponent(start)}&endDate=${encodeURIComponent(end)}&limit=5000`,{headers:{Authorization:`Bearer ${token}`}});
      const billData=await billRes.json();
      setBills(billData.success?(billData.data||[]):[]);
    }catch(e){console.error(e);}
    finally{setLoading(false);}
  },[token,activeFilter,custom]);

  useEffect(()=>{fetchData();},[fetchData]);

  // Build staff stats from bills
  const staffStats=(() => {
    const map={};
    staffList.forEach(s=>{map[s.id]={id:s.id,name:s.name,skillLevel:s.skill_level||'',services:0,revenue:0,helperCount:0,serviceList:[]};});
    bills.forEach(bill=>{
      extractServiceItems(bill).forEach(item=>{
        // resolve staff_id — could be staff_id or attendant_id
        const sid=item.staff_id||item.attendant_id||item.attendant?.id;
        const sname=item.staff_name||item.attendant_name||item.attendant?.name;
        const hid=item.helper_staff_id||item.helper_id||item.helper?.id;
        if(sid){
          if(!map[sid])map[sid]={id:sid,name:sname||`Staff #${sid}`,skillLevel:'',services:0,revenue:0,helperCount:0,serviceList:[]};
          else if(sname&&!map[sid].name)map[sid].name=sname;
          const m=map[sid];
          m.services+=1;m.revenue+=parseFloat(item.price||0);
          const key=`${item.item_name||''}__${item.item_variant||''}`;
          const ex=m.serviceList.find(s=>s.key===key);
          if(ex){ex.count++;ex.revenue+=parseFloat(item.price||0);}
          else m.serviceList.push({key,name:item.item_name||'Service',variant:item.item_variant||null,count:1,revenue:parseFloat(item.price||0)});
        }
        if(hid){
          if(!map[hid])map[hid]={id:hid,name:item.helper_staff_name||item.helper_name||`Staff #${hid}`,skillLevel:'',services:0,revenue:0,helperCount:0,serviceList:[]};
          map[hid].helperCount+=1;
        }
      });
    });
    return Object.values(map).map(m=>({...m,serviceList:m.serviceList.sort((a,b)=>b.count-a.count)}));
  })();

  const sorted=[...staffStats].sort((a,b)=>sortBy==='services'?b.services-a.services:b.revenue-a.revenue);
  const maxRevenue=Math.max(...sorted.map(s=>s.revenue),1);
  const totalServices=staffStats.reduce((s,m)=>s+m.services,0);
  const totalRevenue=staffStats.reduce((s,m)=>s+m.revenue,0);
  const top=sorted[0];
  const currentLabel=rangeLabel(activeFilter,custom);

  return(
    <div className="p-6 lg:p-8 w-full min-h-full">
      <div className="mb-7">
        <h1 className="text-3xl font-black text-gray-900 dark:text-white">Attendant Report</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Staff performance — services done, revenue generated, helper role</p>
      </div>
      <div className="flex flex-wrap gap-2 mb-2">
        {FILTERS.map(f=>(
          <button key={f.id} onClick={()=>{if(f.id==='custom'){setShowCustom(true);return;}setActiveFilter(f.id);setCustom({start:'',end:''}); }}
            className={`px-4 py-2 rounded-xl text-sm font-bold transition-all border-2 ${activeFilter===f.id?'bg-purple-600 border-purple-600 text-white shadow-lg':'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-600 hover:border-purple-300'}`}>
            {f.label}
          </button>
        ))}
      </div>
      {currentLabel&&(
        <div className="flex items-center gap-2 mb-6">
          <Calendar size={13} className="text-purple-500"/>
          <span className="text-xs font-semibold text-purple-600 bg-purple-50 dark:bg-purple-900/20 px-3 py-1 rounded-full">Showing: {currentLabel}</span>
        </div>
      )}
      {loading?(
        <div className="flex flex-col items-center justify-center py-32 gap-4">
          <Loader2 size={36} className="animate-spin text-purple-600"/>
          <p className="text-gray-400 font-medium">Loading attendant data…</p>
        </div>
      ):(
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="relative overflow-hidden rounded-3xl p-6 bg-gradient-to-br from-purple-600 to-violet-700 shadow-sm">
              <div className="absolute -right-4 -top-4 w-20 h-20 rounded-full bg-white/10"/>
              <div className="relative"><div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center mb-3"><Hash size={20} className="text-white"/></div>
                <p className="text-xs font-bold text-white/70 uppercase tracking-widest">Total Services</p>
                <p className="text-2xl font-black text-white mt-1">{totalServices}</p>
                <p className="text-xs text-white/60 mt-1">{staffStats.length} staff members</p>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-3xl p-6 bg-gradient-to-br from-emerald-500 to-teal-600 shadow-sm">
              <div className="absolute -right-4 -top-4 w-20 h-20 rounded-full bg-white/10"/>
              <div className="relative"><div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center mb-3"><TrendingUp size={20} className="text-white"/></div>
                <p className="text-xs font-bold text-white/70 uppercase tracking-widest">Total Revenue</p>
                <p className="text-2xl font-black text-white mt-1">{fmt(totalRevenue)}</p>
                <p className="text-xs text-white/60 mt-1">From all services</p>
              </div>
            </div>
            {top&&top.services>0?(
              <div className="relative overflow-hidden rounded-3xl p-6 bg-gradient-to-br from-amber-500 to-orange-600 shadow-sm">
                <div className="absolute -right-4 -top-4 w-20 h-20 rounded-full bg-white/10"/>
                <div className="relative"><div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center mb-3"><Award size={20} className="text-white"/></div>
                  <p className="text-xs font-bold text-white/70 uppercase tracking-widest">Top Performer</p>
                  <p className="text-xl font-black text-white mt-1 truncate">{top.name}</p>
                  <p className="text-xs text-white/60 mt-1">{top.services} services · {fmt(top.revenue)}</p>
                </div>
              </div>
            ):(
              <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-slate-700 p-6">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Staff Count</p>
                <p className="text-2xl font-black text-gray-800 dark:text-white">{staffList.length}</p>
                <p className="text-xs text-gray-400 mt-1">Registered staff</p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <p className="text-sm font-semibold text-gray-500">Sort by:</p>
            {SORT_OPTIONS.map(s=>(
              <button key={s.id} onClick={()=>setSortBy(s.id)}
                className={`px-4 py-2 rounded-xl text-sm font-bold border-2 ${sortBy===s.id?'bg-purple-600 border-purple-600 text-white':'bg-white dark:bg-slate-800 border-gray-200 text-gray-600 hover:border-purple-300'}`}>
                {s.label}
              </button>
            ))}
          </div>
          <div className="space-y-3">
            {sorted.length===0?(
              <div className="text-center py-20 bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-slate-700">
                <Users size={36} className="mx-auto text-gray-200 mb-3"/>
                <h3 className="text-lg font-black text-gray-400">No data for this period</h3>
              </div>
            ):sorted.map((member,i)=><StaffCard key={member.id} member={member} rank={i+1} maxRevenue={maxRevenue}/>)}
          </div>
        </div>
      )}
      <CustomDateModal open={showCustom} onClose={()=>setShowCustom(false)} onApply={(s,e)=>{setCustom({start:s,end:e});setActiveFilter('custom');}}/>
    </div>
  );
}